import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type { Catalog, ThreadView } from "@/entities/thread";

// 2でsnapshotを追加。3でaction(fetchThread)を追加。
// hook/snapshotの契約はversion 2から変更していないため、既存のuserscriptは
// そのまま動く。
export const AIMOGE_VERSION = 3;
const SLOW_HOOK_WARN_MS = 50;

export const AIMOGE_HOOK_NAMES = [
  "data:thread",
  "data:catalog",
  "data:chunk",
  "data:state",
  "catalog:beforeRender",
  "catalog:rendered",
  "post:beforeRender",
  "post:rendered",
] as const;

export type AimogeHookName = (typeof AIMOGE_HOOK_NAMES)[number];
export type AimogeHookCallback = (value: unknown) => unknown;
export type AimogeQueueEntry = (aimoge: AimogeApi) => void;

export type AimogeSnapshotKind = "thread" | "catalog";

export interface AimogeGetThreadOptions {
  /**
   * true にすると非公開レスとNG対象レスを除いた posts を返す。
   * 未指定・false は生データ(既存の後方互換)。
   * 内部にフィルタが未設置の環境では null を返し console.warn する
   * (生データを黙って返すと、拡張から「NGが効いていない」ことを検出できない
   * ため、除去が目的の呼び出しは fail closed にする)。
   */
  ng?: boolean;
}

/**
 * 表示中のデータを拡張から読み出すpull口。hookが push 型で「登録し損ねると
 * 二度と取れない」のに対し、こちらは任意のタイミングで現在値を取得できる。
 * PC版スレは仮想化でDOMに一部のレスしか無いため、全レス走査はここを使う。
 */
export interface AimogeSnapshot {
  /**
   * 表示中のスレ。未取得・スレ以外の画面ではnull。
   * `{ ng: true }` を渡すと非公開レスとNG対象レスを除いた posts になる
   * (画面と完全一致ではない。post:beforeRender は通さず、showNgContent は
   * 無視する。詳細は docs/extension-hooks.md)。
   */
  getThread(options?: AimogeGetThreadOptions): ThreadView | null;
  /** 表示中のカタログ1本。mixは混合後、個別板はその板のみ。未取得ならnull。 */
  getCatalog(): Catalog | null;
}

/**
 * NG反映済posts組み立ての注入口。shared→featuresの層違反を避けるため、
 * ng-filter は直接importせずここを通す。app.tsxでsetAimogeThreadFilterから
 * 差し込む。差し込まれるまでは `{ ng: true }` は null 応答になる。
 */
export type AimogeThreadFilter = (thread: ThreadView) => ThreadView;

/**
 * スレ本文の再取得を起動する関数の注入口。shared→featuresの層違反を避けるため、
 * useThreadのrefetchを直接importせずここを通す。useThreadのuseEffectで
 * setAimogeThreadFetcherから差し込む。差し込まれるまでは fetchThread() は
 * console.warn を出して no-op(Promise.resolve())を返す。
 */
export type AimogeThreadFetcher = () => Promise<void>;

export interface AimogeApi {
  readonly version: typeof AIMOGE_VERSION;
  register(name: AimogeHookName, callback: AimogeHookCallback): () => void;
  readonly snapshot: AimogeSnapshot;
  /**
   * 表示中のスレを再取得する。スレ画面外(fetcher 未設置)では console.warn を
   * 出して no-op を返す(fail open)。戻りPromiseは refetch 完了で resolve するが、
   * 返り値は無い。最新の値は `aimoge:snapshot` の発火後に `snapshot.getThread()`
   * から読める。連続呼び出しは dedup しない。
   */
  fetchThread(): Promise<void>;
}

export interface AimogeRuntime {
  readonly api: AimogeApi;
  getGeneration(): number;
  subscribe(listener: () => void): () => void;
  transformData<T>(name: AimogeHookName, value: T): T;
  beforeRender<T>(name: AimogeHookName, value: T): T | null;
  rendered(name: AimogeHookName, value: unknown): void;
  drainQueue(queue: readonly unknown[]): void;
  publishSnapshot(kind: "thread", value: ThreadView): void;
  publishSnapshot(kind: "catalog", value: Catalog): void;
  /**
   * `expected` が現在値と同一のときだけnullへ戻す。同じデータを購読する
   * componentが複数ある場合(CatalogPageとCatalogGrid)、片方のunmountで
   * 生存側のsnapshotを消さないためのガード。
   */
  clearSnapshot(kind: AimogeSnapshotKind, expected: unknown): void;
  /**
   * NG反映済posts組み立て関数を差し込む/取り外す。差し込むと
   * `getThread({ ng: true })` が有効になる。null で取り外し。
   * NG設定変更等でフィルタが更新された場合は再度差し込む(呼び出しごとに
   * キャッシュされるため、参照が変わればキャッシュも破棄される)。
   */
  setThreadFilter(filter: AimogeThreadFilter | null): void;
  /**
   * スレ再取得関数を差し込む/取り外す。差し込むと `fetchThread()` が有効に
   * なる。null で取り外し。useThreadの再mountやrefetch identity変化ごとに
   * 差し替える(last-write-wins)。
   */
  setThreadFetcher(fetcher: AimogeThreadFetcher | null): void;
}

type RegisteredHook = {
  id: number;
  callback: AimogeHookCallback;
};

export function createAimogeRuntime(): AimogeRuntime {
  const hooks = new Map<AimogeHookName, RegisteredHook[]>();
  const listeners = new Set<() => void>();
  let generation = 0;
  let nextHookId = 0;
  let threadSnapshot: ThreadView | null = null;
  let catalogSnapshot: Catalog | null = null;
  let threadFilter: AimogeThreadFilter | null = null;
  let threadFetcher: AimogeThreadFetcher | null = null;
  // (source thread, filter) の組を鍵にしたメモ化。同じスレ・同じフィルタで
  // 繰り返し呼ばれても組み立て結果を使い回す。片方でも変わればキャッシュ破棄。
  let filteredThreadCache: {
    source: ThreadView;
    filter: AimogeThreadFilter;
    result: ThreadView;
  } | null = null;

  const notifySnapshotChange = (kind: AimogeSnapshotKind): void => {
    if (typeof document === "undefined") return;
    document.dispatchEvent(
      new CustomEvent("aimoge:snapshot", { detail: { kind } }),
    );
  };

  const notifyGenerationChange = (): void => {
    generation += 1;
    for (const listener of listeners) listener();
  };

  const register = (
    name: AimogeHookName,
    callback: AimogeHookCallback,
  ): (() => void) => {
    const registeredHook: RegisteredHook = { id: nextHookId, callback };
    nextHookId += 1;
    const registered = hooks.get(name) ?? [];
    registered.push(registeredHook);
    hooks.set(name, registered);
    notifyGenerationChange();

    return (): void => {
      const current = hooks.get(name);
      if (!current) return;
      const next = current.filter((hook) => hook.id !== registeredHook.id);
      if (next.length === current.length) return;
      if (next.length === 0) hooks.delete(name);
      else hooks.set(name, next);
      notifyGenerationChange();
    };
  };

  const getThread = (options?: AimogeGetThreadOptions): ThreadView | null => {
    if (!options?.ng) return threadSnapshot;
    if (!threadSnapshot) return null;
    if (!threadFilter) {
      console.warn(
        "[aimoge] getThread({ ng: true }) はフィルタ未設置のため null を返します",
      );
      return null;
    }
    if (
      filteredThreadCache &&
      filteredThreadCache.source === threadSnapshot &&
      filteredThreadCache.filter === threadFilter
    ) {
      return filteredThreadCache.result;
    }
    const result = threadFilter(threadSnapshot);
    filteredThreadCache = {
      source: threadSnapshot,
      filter: threadFilter,
      result,
    };
    return result;
  };

  const fetchThread = (): Promise<void> => {
    if (!threadFetcher) {
      console.warn(
        "[aimoge] fetchThread() はfetcher未設置のためno-opです(スレ画面外か初期化前)",
      );
      return Promise.resolve();
    }
    try {
      return threadFetcher();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const api: AimogeApi = {
    version: AIMOGE_VERSION,
    register,
    snapshot: {
      getThread,
      getCatalog: (): Catalog | null => catalogSnapshot,
    },
    fetchThread,
  };

  const invoke = (
    name: AimogeHookName,
    hook: RegisteredHook,
    value: unknown,
  ): unknown => {
    const startedAt = performance.now();
    try {
      return hook.callback(value);
    } catch (error) {
      console.warn(`[aimoge] hook ${name}#${hook.id} failed`, error);
      return undefined;
    } finally {
      const elapsedMs = performance.now() - startedAt;
      if (elapsedMs > SLOW_HOOK_WARN_MS) {
        console.warn(
          `[aimoge] slow hook ${name}#${hook.id}: ${elapsedMs.toFixed(1)}ms`,
        );
      }
    }
  };

  const getHooks = (name: AimogeHookName): RegisteredHook[] => [
    ...(hooks.get(name) ?? []),
  ];

  return {
    api,
    getGeneration: (): number => generation,
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    transformData: <T>(name: AimogeHookName, value: T): T => {
      let current: unknown = value;
      for (const hook of getHooks(name)) {
        const result = invoke(name, hook, current);
        if (result !== undefined) current = result;
      }
      return current as T;
    },
    beforeRender: <T>(name: AimogeHookName, value: T): T | null => {
      let current: unknown = value;
      for (const hook of getHooks(name)) {
        const result = invoke(name, hook, current);
        if (result === null) return null;
        if (result !== undefined) current = result;
      }
      return current as T;
    },
    rendered: (name: AimogeHookName, value: unknown): void => {
      for (const hook of getHooks(name)) invoke(name, hook, value);
    },
    drainQueue: (queue: readonly unknown[]): void => {
      for (const entry of queue) {
        if (typeof entry !== "function") {
          console.warn("[aimoge] ignored a non-function queue entry");
          continue;
        }
        try {
          entry(api);
        } catch (error) {
          console.warn("[aimoge] queued extension failed", error);
        }
      }
    },
    publishSnapshot: (
      kind: AimogeSnapshotKind,
      value: ThreadView | Catalog,
    ): void => {
      // 同一参照の再publishは通知しない。同じqueryを購読するcomponentが
      // 複数あっても拡張側のイベントは1回で済む。
      if (kind === "thread") {
        if (Object.is(threadSnapshot, value)) return;
        threadSnapshot = value as ThreadView;
      } else {
        if (Object.is(catalogSnapshot, value)) return;
        catalogSnapshot = value as Catalog;
      }
      notifySnapshotChange(kind);
    },
    clearSnapshot: (kind: AimogeSnapshotKind, expected: unknown): void => {
      if (kind === "thread") {
        if (threadSnapshot === null || !Object.is(threadSnapshot, expected))
          return;
        threadSnapshot = null;
        // sourceが失われたキャッシュは破棄。次のスレをこの古い参照で
        // 誤って返してしまうのを防ぐ。
        filteredThreadCache = null;
      } else {
        if (catalogSnapshot === null || !Object.is(catalogSnapshot, expected))
          return;
        catalogSnapshot = null;
      }
      notifySnapshotChange(kind);
    },
    setThreadFilter: (filter: AimogeThreadFilter | null): void => {
      threadFilter = filter;
      filteredThreadCache = null;
    },
    setThreadFetcher: (fetcher: AimogeThreadFetcher | null): void => {
      threadFetcher = fetcher;
    },
  };
}

const runtime: AimogeRuntime = createAimogeRuntime();
let initialized = false;

export function initializeAimoge(): AimogeApi {
  if (initialized && window.aimoge) return window.aimoge;
  initialized = true;

  const queued = window.aimogeQueue ?? [];
  window.aimoge = runtime.api;
  window.aimogeQueue = [];
  runtime.drainQueue(queued);
  document.dispatchEvent(
    new CustomEvent("aimoge:ready", {
      detail: { aimoge: runtime.api, version: AIMOGE_VERSION },
    }),
  );
  return runtime.api;
}

export function runAimogeDataHook<T>(name: AimogeHookName, value: T): T {
  return runtime.transformData(name, value);
}

/**
 * `getThread({ ng: true })` が返す組み立て関数を差し込む(取り外しは null)。
 * shared→featuresの層違反を避けるため、app.tsxから呼ぶ想定。
 * NG設定の変更で関数の参照が変われば内部キャッシュも破棄されるため、
 * ng-filter store の subscribe と組み合わせて使う。
 */
export function setAimogeThreadFilter(filter: AimogeThreadFilter | null): void {
  runtime.setThreadFilter(filter);
}

/**
 * `fetchThread()` が呼ぶ再取得関数を差し込む(取り外しは null)。
 * shared→featuresの層違反を避けるため、useThreadのuseEffectから呼ぶ想定。
 * useThreadのrefetch参照が変わればここに差し替える(last-write-wins)。
 */
export function setAimogeThreadFetcher(
  fetcher: AimogeThreadFetcher | null,
): void {
  runtime.setThreadFetcher(fetcher);
}

export function runAimogeBeforeRender<T>(
  name: "catalog:beforeRender" | "post:beforeRender",
  value: T,
): T | null {
  return runtime.beforeRender(name, value);
}

export function useAimogeHookGeneration(): number {
  return useSyncExternalStore(
    runtime.subscribe,
    runtime.getGeneration,
    runtime.getGeneration,
  );
}

export function useAimogeBeforeRender<T>(
  name: "catalog:beforeRender" | "post:beforeRender",
  value: T,
  alreadyPrepared: boolean = false,
): T | null {
  const generation = useAimogeHookGeneration();
  return useMemo(() => {
    void generation;
    return alreadyPrepared ? value : runAimogeBeforeRender(name, value);
  }, [alreadyPrepared, generation, name, value]);
}

export function useAimogeRendered<
  TElement extends HTMLElement = HTMLDivElement,
>(
  name: "catalog" | "post",
  value: unknown,
  threadId?: string,
): React.RefObject<TElement | null> {
  const elementRef = useRef<TElement | null>(null);
  const didRenderRef = useRef(false);

  useEffect(() => {
    if (value == null) {
      didRenderRef.current = false;
      return;
    }
    const element = elementRef.current;
    if (!element || didRenderRef.current) return;

    const payload =
      name === "post"
        ? { post: value, element, threadId: threadId ?? "" }
        : { thread: value, element, threadId: threadId ?? "" };
    runtime.rendered(`${name}:rendered`, payload);
    didRenderRef.current = true;
  }, [name, threadId, value]);

  return elementRef;
}

function useAimogeSnapshot(
  kind: AimogeSnapshotKind,
  value: ThreadView | Catalog | undefined,
): void {
  useEffect(() => {
    if (value === undefined) return;
    if (kind === "thread") runtime.publishSnapshot(kind, value as ThreadView);
    else runtime.publishSnapshot(kind, value as Catalog);
    return (): void => {
      runtime.clearSnapshot(kind, value);
    };
  }, [kind, value]);
}

/** 表示中のスレを`aimoge.snapshot.getThread()`へ公開する。 */
export function useAimogeThreadSnapshot(value: ThreadView | undefined): void {
  useAimogeSnapshot("thread", value);
}

/** 表示中のカタログを`aimoge.snapshot.getCatalog()`へ公開する。 */
export function useAimogeCatalogSnapshot(value: Catalog | undefined): void {
  useAimogeSnapshot("catalog", value);
}

declare global {
  interface Window {
    aimoge?: AimogeApi;
    aimogeQueue?: AimogeQueueEntry[];
  }
}
